import { DayPlanSettings, DEFAULT_SEASON_FACTORS } from "./dayPlan";
import { DEFAULT_WEATHER_FACTORS, WeatherFactors } from "./observedWeather";

/** Same thresholds as the tool default; only the multipliers vary per profile. */
function weatherFactors(multipliers: {
  rain?: number;
  sun?: number;
  warm?: number;
  wind?: number;
}): WeatherFactors {
  return {
    rain: {
      ...DEFAULT_WEATHER_FACTORS.rain,
      multiplier: multipliers.rain ?? 1,
    },
    sun: { ...DEFAULT_WEATHER_FACTORS.sun, multiplier: multipliers.sun ?? 1 },
    warm: {
      ...DEFAULT_WEATHER_FACTORS.warm,
      multiplier: multipliers.warm ?? 1,
    },
    wind: {
      ...DEFAULT_WEATHER_FACTORS.wind,
      multiplier: multipliers.wind ?? 1,
    },
  };
}

export interface MerchantProfilePreset {
  id: string;
  label: string;
  /** A complete, runnable plan — every field the day-plan panel reads is set. */
  settings: DayPlanSettings;
}

/**
 * Five starting shapes for the day-plan panel's merchant-profile picker.
 *
 * Every value stays editable after picking one — a preset is a starting point,
 * not a mode. Profiles 1 and 5 are deliberate negative controls (no weather
 * response, no weekday response), proving the forecast doesn't invent drivers.
 */
export const MERCHANT_PROFILE_PRESETS: MerchantProfilePreset[] = [
  {
    id: "office-lunch",
    label: "Office-district lunch",
    settings: {
      baseRevenue: 900,
      salesPerDay: 45,
      tipRatePercent: 5,
      growthPerMonth: 1,
      noise: 0.06,
      holidayFactor: 0.25,
      seasonFactors: DEFAULT_SEASON_FACTORS,
      weekdayShape: {
        0: 0.1,
        1: 1.25,
        2: 1.25,
        3: 1.25,
        4: 1.25,
        5: 1.1,
        6: 0.3,
      },
      weatherFactors: weatherFactors({ rain: 0.97 }),
      eventRules: [],
    },
  },
  {
    id: "food-truck",
    label: "Food truck",
    settings: {
      baseRevenue: 500,
      salesPerDay: 60,
      tipRatePercent: 12,
      growthPerMonth: 1,
      noise: 0.2,
      holidayFactor: 1.4,
      seasonFactors: DEFAULT_SEASON_FACTORS,
      weekdayShape: {
        0: 1.4,
        1: 0.7,
        2: 0.85,
        3: 0.85,
        4: 0.85,
        5: 0.85,
        6: 1.6,
      },
      weatherFactors: weatherFactors({
        rain: 0.45,
        sun: 0.8,
        warm: 1.3,
        wind: 0.7,
      }),
      eventRules: [],
    },
  },
  {
    id: "terrace-cafe",
    label: "Terrace café",
    settings: {
      baseRevenue: 800,
      salesPerDay: 50,
      tipRatePercent: 10,
      growthPerMonth: 1,
      noise: 0.1,
      holidayFactor: 1.2,
      seasonFactors: { ...DEFAULT_SEASON_FACTORS, summer: 1.2, autumn: 0.7 },
      weekdayShape: {
        0: 1.3,
        1: 0.95,
        2: 0.95,
        3: 0.95,
        4: 0.95,
        5: 0.95,
        6: 1.45,
      },
      weatherFactors: weatherFactors({
        rain: 0.55,
        sun: 0.85,
        warm: 1.35,
        wind: 0.85,
      }),
      eventRules: [],
    },
  },
  {
    id: "neighbourhood-bakery",
    label: "Neighbourhood bakery",
    settings: {
      baseRevenue: 350,
      salesPerDay: 40,
      tipRatePercent: 2,
      growthPerMonth: 1,
      noise: 0.08,
      holidayFactor: 1.25,
      seasonFactors: DEFAULT_SEASON_FACTORS,
      // Sunday closed: a 0 weekday factor omits the day entirely.
      weekdayShape: {
        0: 0,
        1: 0.95,
        2: 0.95,
        3: 0.95,
        4: 0.95,
        5: 0.95,
        6: 1.5,
      },
      weatherFactors: weatherFactors({ rain: 0.9 }),
      eventRules: [],
    },
  },
  {
    id: "hotel-tourist",
    label: "Hotel / tourist",
    settings: {
      baseRevenue: 1500,
      salesPerDay: 30,
      tipRatePercent: 15,
      growthPerMonth: 1.08,
      noise: 0.05,
      holidayFactor: 1.5,
      seasonFactors: DEFAULT_SEASON_FACTORS,
      // Flat on purpose: the negative control for weekday response.
      weekdayShape: { 0: 1, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1 },
      weatherFactors: weatherFactors({ rain: 0.95 }),
      eventRules: [],
    },
  },
];
